import React from "react";
import {
  DataGrid,
  GridColDef,
  GridRowId,
} from "@mui/x-data-grid";
import CustomPagination from "../pagination/Pagination";
import "./Table.css";
import { observer } from "mobx-react";
import { Checkbox } from "@mui/material";
import AddCircleIcon from "../icons/AddCircleIcon";
import RemoveCircleIcon from "../icons/RemoveCircleIcon";

type Props = {
  rows: any[];
  columns: GridColDef[];
  handleSortModelChange?: (field: string, sort: string) => void;
  isLoading?: boolean;
  withCheckbox?: boolean;
  withAllCheckbox?: boolean;
  selectedRowsData?: GridRowId[];
  pageSize?: number;
  setPageSize?: (value: number) => void;
  page?: number;
  setPage?: (value: number) => void;
  totalRecordsInDB?: number;
  totalFiltered?: number;
  paginationMode?: "client" | "server";

  onRowUnselected?: (id: GridRowId) => void;
  onRowSelected?: (id: GridRowId) => void;
};

const MAX_PAGE_SIZE = 100;

const disableSelectAllCheckboxCSS = {
  "& .MuiDataGrid-columnHeaderCheckbox .MuiDataGrid-columnHeaderTitleContainer": {
    display: "none",
  },
};

function DataTable(props: Props) {
  const {
    rows,
    columns,
    page = 0,
    setPage,
    pageSize = 100,
    setPageSize,
    totalRecordsInDB,
    totalFiltered,
    isLoading,
    paginationMode = "server",
  } = props;

  const safePageSize = Math.min(Math.max(pageSize, 1), MAX_PAGE_SIZE);
  const totalRows = totalFiltered ?? totalRecordsInDB ?? rows.length;

  const handlePageSizeChange = (value: number) => {
    const newPageSize = Math.min(Math.max(value, 1), MAX_PAGE_SIZE);
    setPageSize?.(newPageSize);
    setPage?.(0);
  };

  return (
    <div className="table-container">
      <DataGrid
        className="non-clickable-headers"
        sx={{ ...disableSelectAllCheckboxCSS }}
        rows={rows}
        columns={columns}
        getRowId={(row) => row.id}
        pagination
        paginationMode={paginationMode}
        rowCount={totalRows}
        page={page}
        pageSize={safePageSize}
        loading={isLoading}
        onPageChange={(newPage) => setPage?.(newPage)}
        onPageSizeChange={handlePageSizeChange}
        {...({ disableSelectionOnClick: true } as any)}
        {...({ disableRowSelectionOnClick: true } as any)}
        components={{
          Pagination: () => (
            <CustomPagination
              page={page}
              setPage={setPage}
              pageSize={safePageSize}
              setPageSize={handlePageSizeChange}
              totalFiltered={totalRows}
            />
          ),
          NoRowsOverlay: () => <div />,
          BaseCheckbox: React.forwardRef((p: any, ref: any) => (
            <Checkbox
              icon={<AddCircleIcon />}
              checkedIcon={<RemoveCircleIcon />}
              {...p}
              ref={ref}
            />
          )),
        }}
      />
    </div>
  );
}

export default observer(DataTable);